import React, { useCallback, useEffect, useState } from 'react';
import { Steps, Form, Input, Switch, Button, Alert, Spin } from 'antd';
import { message } from '../services/toastMessage';
import { useTranslation } from 'react-i18next';
import { loadConnectAndInitialize, StripeConnectInstance } from '@stripe/connect-js';
import { ConnectComponentsProvider, ConnectAccountOnboarding } from '@stripe/react-connect-js';
import AvatarUpload from './AvatarUpload';
import EmojiTextArea from './EmojiTextArea';
import IdentityLegalStep, { extractCompleteBillingIdentity } from './IdentityLegalStep';
import { useHostProfile } from '../contextProviders/HostProfileProvider';
import { createServiceProviderProfile, startServiceProviderVerification } from '../services/ServiceProviderProfileService';
import { getStripeConfig } from '../services/StripeService';
import { uploadPhotos } from '../services/MediaService';
import { IServiceProviderProfile, ICreateServiceProviderProfileRequest } from '../interfaces/heavyDutyMachinery/IServiceProviderProfile';
import { HostAccountType } from '../interfaces/heavyDutyMachinery/IHostProfile';
import { CONNECT_APPEARANCE } from '../constants/stripeConnectAppearance';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

interface ServiceProviderOnboardingWizardProps {
  /** Caller already has a ServiceProviderProfile (see ServiceProviderOnboardingPage's own read of useServiceProviderProfile) -- skip straight to the payments step, there's nothing left to create. */
  existingProfile?: IServiceProviderProfile;
  onComplete: () => void;
  onCancel?: () => void;
}

interface ProfileFormValues {
  accountType?: HostAccountType;
  legalName?: string;
  taxId?: string;
  contactPhone?: string;
  billingAddress?: string;
  fullName: string;
  displayName: string;
  profilePhotoUrl?: string;
  bio?: string;
  sharedAvailability: boolean;
}

const STEP_KEYS = ['identity', 'profile', 'payments'] as const;
type StepKey = (typeof STEP_KEYS)[number];

const STEP_TITLE_KEYS: Record<StepKey, string> = {
  // Same shared title as HostOnboardingWizard's own 'identity' step -- it's
  // literally the same IdentityLegalStep component/fields.
  identity: TRANSLATIONS.HOST_ONBOARDING_STEP_LEGAL,
  profile: TRANSLATIONS.SERVICE_PROVIDER_ONBOARDING_STEP_PROFILE,
  payments: TRANSLATIONS.SERVICE_PROVIDER_ONBOARDING_STEP_PAYMENTS,
};

/**
 * First-run wizard for ServiceProviderProfile creation, self-registration/
 * independent modality only -- same "a single Form instance, only the final
 * step of each phase actually posts anything" spirit as HostOnboardingWizard,
 * but unlike that one, this deliberately chains straight into Stripe Connect
 * onboarding (see this component's own doc comment on 'payments' below)
 * rather than leaving it for a separate Settings tab the way HostProfile's
 * own PayoutsTab does -- there is no ServiceProvider-facing Settings section
 * yet, and testing the Fase 13 multi-party payment flow needs a verified
 * Connect account to exist at all.
 */
const ServiceProviderOnboardingWizard: React.FC<ServiceProviderOnboardingWizardProps> = ({ existingProfile, onComplete, onCancel }) => {
  const { t } = useTranslation();
  // A complete UserBillingProfile already on file via this same person's
  // HostProfile (if any) -- see IdentityLegalStep's own doc comment on
  // existingBilling for why this makes that step a read-only summary
  // instead of the usual form.
  const { hostProfile, loading: hostProfileLoading } = useHostProfile();
  const existingBilling = extractCompleteBillingIdentity(hostProfile);
  const [form] = Form.useForm<ProfileFormValues>();
  const [stepKey, setStepKey] = useState<StepKey>(existingProfile ? 'payments' : 'identity');
  const [submitting, setSubmitting] = useState(false);
  const [profile, setProfile] = useState<IServiceProviderProfile | undefined>(existingProfile);
  const photoUrl = Form.useWatch('profilePhotoUrl', form);

  const [connectInstance, setConnectInstance] = useState<StripeConnectInstance | undefined>(undefined);
  const [connectStarting, setConnectStarting] = useState(false);
  const [connectError, setConnectError] = useState(false);

  // accountType tracked via onValuesChange rather than Form.useWatch -- same
  // "stops reflecting the real value once its Form.Item unmounts" reasoning
  // as HostOnboardingWizard's own identical comment; IdentityLegalStep's own
  // dynamic Individual/Business labels need this to stay correct once the
  // wizard has moved past the 'identity' step.
  const [accountType, setAccountType] = useState<HostAccountType>('Individual');
  const handleValuesChange = (changedValues: Partial<ProfileFormValues>) => {
    if ('accountType' in changedValues) setAccountType(changedValues.accountType ?? 'Individual');
  };

  // Same "existingBilling's AccountType can never be re-asked, seed it as
  // soon as it resolves" reasoning as HostOnboardingWizard's own identical effect.
  useEffect(() => {
    if (existingBilling) setAccountType(existingBilling.accountType);
  }, [existingBilling]);

  const fieldsForStep: Record<StepKey, (keyof ProfileFormValues)[]> = {
    // Nothing to validate once existingBilling already reuses the other
    // role's row -- IdentityLegalStep doesn't render these Form.Items at
    // all in that case (see its own doc comment on existingBilling).
    identity: existingBilling ? [] : ['accountType', 'legalName', 'contactPhone', 'billingAddress'],
    profile: ['fullName', 'displayName', 'bio', 'sharedAvailability'],
    payments: [],
  };

  const currentIndex = STEP_KEYS.indexOf(stepKey);
  const isVerified = profile?.idVerificationStatus === 'Verified';

  const goNext = async () => {
    try {
      await form.validateFields(fieldsForStep.identity);
      setStepKey('profile');
    } catch {
      // antd already highlights the invalid fields.
    }
  };

  const goBack = () => setStepKey('identity');

  const handleCreateProfile = async () => {
    try {
      await form.validateFields(fieldsForStep.profile);
    } catch {
      return;
    }

    const values = form.getFieldsValue(true);
    const request: ICreateServiceProviderProfileRequest = {
      fullName: values.fullName,
      accountType: values.accountType!,
      legalName: values.legalName!,
      taxId: values.taxId,
      contactPhone: values.contactPhone!,
      billingAddress: values.billingAddress!,
      displayName: values.displayName,
      profilePhotoUrl: values.profilePhotoUrl,
      bio: values.bio,
      sharedAvailability: values.sharedAvailability,
    };

    setSubmitting(true);
    try {
      const created = await createServiceProviderProfile(request);
      setProfile(created);
      message.success(translate(t, TRANSLATIONS.SERVICE_PROVIDER_ONBOARDING_SUCCESS));
      setStepKey('payments');
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.SERVICE_PROVIDER_ONBOARDING_ERROR));
    } finally {
      setSubmitting(false);
    }
  };

  // Same loadConnectAndInitialize/fetchClientSecret pattern as
  // PaymentsSection's own PayoutsTab -- this first call is what actually
  // creates the provider's Stripe Connect Express account server-side.
  const initializeConnect = useCallback(async () => {
    setConnectStarting(true);
    setConnectError(false);
    try {
      const { publishableKey } = await getStripeConfig();
      const instance = loadConnectAndInitialize({
        publishableKey,
        fetchClientSecret: async () => {
          const result = await startServiceProviderVerification();
          setProfile(result.profile);
          return result.accountSessionClientSecret ?? '';
        },
        appearance: CONNECT_APPEARANCE,
      });
      setConnectInstance(instance);
    } catch {
      setConnectError(true);
    } finally {
      setConnectStarting(false);
    }
  }, []);

  // Auto-starts the moment the payments step is reached -- unlike
  // PayoutsTab's own NotStarted branch (which waits for an explicit click,
  // since a host can land on that Settings tab for reasons unrelated to
  // onboarding), this step exists ONLY to onboard payments, so there is
  // nothing worth gating behind an extra click.
  useEffect(() => {
    if (stepKey === 'payments' && !isVerified && !connectInstance && !connectStarting) {
      initializeConnect();
    }
  }, [stepKey, isVerified, connectInstance, connectStarting, initializeConnect]);

  // Blocks on the OTHER role's own profile fetch before deciding whether
  // the identity step opens as a blank form or a read-only summary -- same
  // "gate on loading before rendering" convention HostOnboardingWizard's own
  // identical check uses.
  if (hostProfileLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <div>
        <Steps
          current={currentIndex}
          size="small"
          style={{ marginBottom: 28 }}
          items={STEP_KEYS.map((key) => ({ title: translate(t, STEP_TITLE_KEYS[key]) }))}
        />

        {stepKey === 'identity' && (
          <>
            <Form form={form} layout="vertical" initialValues={{ accountType: 'Individual' }} onValuesChange={handleValuesChange}>
              <IdentityLegalStep form={form} submitting={submitting} accountType={accountType} requireTaxId={false} existingBilling={existingBilling} />
            </Form>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28 }}>
              <div>
                {onCancel && (
                  <Button shape="round" onClick={onCancel} disabled={submitting}>
                    {translate(t, TRANSLATIONS.HOST_ONBOARDING_CANCEL)}
                  </Button>
                )}
              </div>
              <Button type="primary" shape="round" onClick={goNext}>
                {translate(t, TRANSLATIONS.HOST_ONBOARDING_NEXT)}
              </Button>
            </div>
          </>
        )}

        {stepKey === 'profile' && (
          <>
            <Form form={form} layout="vertical" initialValues={{ sharedAvailability: true }}>
              <Form.Item label={translate(t, TRANSLATIONS.HOST_FIELD_PROFILE_PHOTO)}>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <AvatarUpload
                    value={photoUrl}
                    onChange={(url) => form.setFieldsValue({ profilePhotoUrl: url })}
                    uploadFn={(file) => uploadPhotos('serviceProviders', [file]).then((uploaded) => uploaded[0].url)}
                    disabled={submitting}
                  />
                </div>
              </Form.Item>
              <Form.Item name="profilePhotoUrl" hidden>
                <Input />
              </Form.Item>
              <Form.Item name="fullName" label={translate(t, TRANSLATIONS.SERVICE_PROVIDER_FIELD_FULL_NAME)} rules={[{ required: true }]}>
                <Input disabled={submitting} />
              </Form.Item>
              <Form.Item
                name="displayName"
                label={translate(t, TRANSLATIONS.SERVICE_PROVIDER_FIELD_DISPLAY_NAME)}
                extra={translate(t, TRANSLATIONS.SERVICE_PROVIDER_FIELD_DISPLAY_NAME_HINT)}
                rules={[{ required: true }]}
              >
                <Input disabled={submitting} />
              </Form.Item>
              <Form.Item name="bio" label={translate(t, TRANSLATIONS.SERVICE_PROVIDER_FIELD_BIO)}>
                <EmojiTextArea rows={3} maxLength={1000} disabled={submitting} />
              </Form.Item>
              <Form.Item
                name="sharedAvailability"
                label={translate(t, TRANSLATIONS.SERVICE_PROVIDER_FIELD_SHARED_AVAILABILITY)}
                extra={translate(t, TRANSLATIONS.SERVICE_PROVIDER_FIELD_SHARED_AVAILABILITY_HINT)}
                valuePropName="checked"
              >
                <Switch disabled={submitting} />
              </Form.Item>
            </Form>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28 }}>
              <Button shape="round" onClick={goBack} disabled={submitting}>
                {translate(t, TRANSLATIONS.HOST_ONBOARDING_BACK)}
              </Button>
              <Button type="primary" shape="round" onClick={handleCreateProfile} loading={submitting}>
                {translate(t, TRANSLATIONS.HOST_ONBOARDING_NEXT)}
              </Button>
            </div>
          </>
        )}

        {stepKey === 'payments' && (
          <>
            <p style={{ marginTop: 0, color: 'rgba(0,0,0,0.55)' }}>{translate(t, TRANSLATIONS.SERVICE_PROVIDER_ONBOARDING_PAYMENTS_INTRO)}</p>

            {connectError && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translate(t, TRANSLATIONS.SETTINGS_PAYOUTS_ERROR)} />}

            {isVerified ? (
              <p>{translate(t, TRANSLATIONS.SERVICE_PROVIDER_ONBOARDING_ALREADY_VERIFIED)}</p>
            ) : connectInstance ? (
              // width:'100%' gives the embedded iframe a real box to measure
              // on its first render -- same fix as PayoutsTab's own identical comment.
              <div style={{ width: '100%' }}>
                <ConnectComponentsProvider connectInstance={connectInstance}>
                  <ConnectAccountOnboarding onExit={onComplete} />
                </ConnectComponentsProvider>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 16 }}>
                <Spin />
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 28 }}>
              <Button shape="round" onClick={onComplete} disabled={connectStarting}>
                {translate(t, isVerified ? TRANSLATIONS.SERVICE_PROVIDER_ONBOARDING_DONE_BUTTON : TRANSLATIONS.SERVICE_PROVIDER_ONBOARDING_SKIP_STRIPE)}
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ServiceProviderOnboardingWizard;
